import React, { useContext, useEffect, useState, } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import {
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { IntroOffer } from './intro-offer/intro-offer.component';
import { PaymentEstimator } from './payment-estimator/payment-estimator.component';
import { OffersForYou } from './offers-for-you/offers-for-you.component';
import './consumer-card-messaging.style.scss';

const { bool, shape, string, number } = proptypes;

const isValidItemId = (itemId) => /^\d{9}$/.test(itemId);

const isValidBundlePrice = (bundlePrice) => {
  return Boolean(
    bundlePrice?.length
  );
};
const isValidProps = (props) => {
  const { itemId, bundlePrice } = props;
  return isValidItemId(itemId) || isValidBundlePrice(bundlePrice);
};

const isValidData = (data) => {
  return !!(data?.product?.pricing);
};

const isValidProduct = (product) => !!product?.pricing && !!product?.identifiers;

export const ConsumerCardMessaging = (props) => {

  const theme = useTheme(ConsumerCardMessaging, props);
  const { showHDCC } = theme.props;

  const { channel } = useContext(ExperienceContext);
  const { isLocalized } = useStore();
  const generalThreshold = parseFloat(useConfigService('PipPaymentEstimatorThreshold')) || 850.00;
  const applianceThreshold = parseFloat(useConfigService('AppliancePipPaymentEstimatorThreshold')) || 300.00;
  const plpThreshold = 300.00;

  const [hasDataFullyLoaded, setHasDataFullyLoaded] = useState(false);

  const {
    paymentEstimatorCampaignCode,
    applyNowCampaignCode,
    itemId,
    product,
    bundlePrice,
    isAppliance,
    clsRemediation,
    isB2B,
    showEverydayFinancing,
    isPlp,
    hideConsumerCardMessaging
  } = props;

   if (hideConsumerCardMessaging) {
    return null;
  }

  const { configuredPrice } = useLifeCycleEventBus('configurator.configurator_pricing_event');
  const hasValidBundlePrice = isValidBundlePrice(bundlePrice);
  const hasValidProduct = isValidProduct(product);
  const hasValidProps = isValidProps(props) || showEverydayFinancing || hasValidBundlePrice || hasValidProduct;

  const { data, loading } = useDataModel('product', {
    variables: { itemId },
    skip: !hasValidProps || hasValidBundlePrice || hasValidProduct
  });
  
  useEffect(() => {
    setHasDataFullyLoaded(!loading && isLocalized);
  }, [loading, isLocalized]);

  const hasValidData = showEverydayFinancing || isValidData(data) || hasValidBundlePrice || hasValidProduct;

  if (!hasValidProps) {
    return null;
  }

  if (!hasValidData) {
    const { placeholders, preservePlaceholders } = clsRemediation;
    if (!placeholders) {
      return null;
    }
    if (hasDataFullyLoaded && !preservePlaceholders) {
      return null;
    }
  }

  const productData = hasValidProduct ? product : data?.product;
  const isProductAppliance = productData?.identifiers?.productType === 'MAJOR_APPLIANCE';
  const pipPaymentEstimatorThreshold = (isAppliance || isProductAppliance) ? applianceThreshold : generalThreshold;
  const value = configuredPrice?.pricing?.value || productData?.pricing?.value;
  const unitOfMeasure = configuredPrice?.pricing?.unitOfMeasure || productData?.pricing?.unitOfMeasure;
  const alternatePriceDisplay = configuredPrice?.pricing?.alternatePriceDisplay
    || productData?.pricing?.alternatePriceDisplay;
  const alternateUnitOfMeasure = alternatePriceDisplay && unitOfMeasure.toLowerCase() !== 'each';
  const showPaymentEstimator = (isPlp && value >= plpThreshold)
    || showEverydayFinancing
    || (!isB2B && !hasValidBundlePrice && (
      value >= pipPaymentEstimatorThreshold
      || (isPlp && value === null)
    ));
  const showIntroOfferB2C = !hasValidBundlePrice && !showPaymentEstimator && value >= 25.0
    && value < pipPaymentEstimatorThreshold && !isB2B;
  const showIntroOfferB2B = !hasValidBundlePrice && !showPaymentEstimator && (value >= 25.0 || value === null) && isB2B;
  const hasCampaignCode = !!paymentEstimatorCampaignCode || !!applyNowCampaignCode;
  const showConsumerCard = hasCampaignCode
    && (hasValidBundlePrice || showPaymentEstimator || (showIntroOfferB2C || showIntroOfferB2B))
    && showHDCC;
  if (!showConsumerCard) {
    return <div className="consumer-card-messaging--hidden"></div>;
  }

  const consumerCardClasses = classNames({
    'consumer-card-messaging--hidden': !hasValidData || !hasDataFullyLoaded
  });

  return (
    <div className={consumerCardClasses} data-component="ConsumerCardMessaging">
      {hasValidBundlePrice && (
        <OffersForYou
          paymentEstimatorCampaignCode={paymentEstimatorCampaignCode}
          defaultCampaignCode={applyNowCampaignCode}
          price={bundlePrice}
          isMobile={channel === 'mobile'}
          isAppliance={isAppliance}
        />
      )}
      {showPaymentEstimator && (
        <PaymentEstimator
          campaignCode={paymentEstimatorCampaignCode}
          price={value}
          isMobile={channel === 'mobile'}
          isAppliance={isProductAppliance}
          showEverydayFinancing={showEverydayFinancing || value === null}
          isPlp={isPlp}
        />
      )}
      {(showIntroOfferB2C || showIntroOfferB2B) && (
        <IntroOffer
          campaignCode={applyNowCampaignCode}
          price={value}
          isB2B={isB2B}
          mobile={channel === 'mobile'}
          showGenericDiscount={alternateUnitOfMeasure || value === null}
          isPlp={isPlp}
        />
      )}
    </div>
  );
};

ConsumerCardMessaging.displayName = 'ConsumerCardMessaging';

ConsumerCardMessaging.dataModel = {
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    dataSources: stringType(),
    pricing: params({ storeId: stringType(), isBrandPricingPolicyCompliant: boolType() }).shape({
      value: numberType(),
      unitOfMeasure: stringType(),
      alternatePriceDisplay: boolType()
    }),
    identifiers: shapeType({
      productType: stringType()
    })
  })
};

const checkRequiredProps = (props) => {
  if (props.showEverydayFinancing) {
    return null;
  }
  if (!props.itemId && !props.bundlePrice && !props.product) {
    return new Error(
      'The \'itemId\', \'bundlePrice\' or \'product\' is required by the consumer-card-messaging component.'
    );
  }
  return null;
};

ConsumerCardMessaging.propTypes = {
  paymentEstimatorCampaignCode: string,
  applyNowCampaignCode: string,
  itemId: checkRequiredProps,
  product: checkRequiredProps,
  bundlePrice: checkRequiredProps,
  isAppliance: bool,
  clsRemediation: shape({
    placeholders: bool,
    preservePlaceholders: bool
  }),
  isB2B: bool,
  showEverydayFinancing: bool,
  isPlp: bool,
  hideConsumerCardMessaging: bool
};

ConsumerCardMessaging.defaultProps = {
  paymentEstimatorCampaignCode: null,
  applyNowCampaignCode: null,
  itemId: null,
  product: null,
  bundlePrice: null,
  isAppliance: false,
  clsRemediation: {
    placeholders: false,
    preservePlaceholders: false
  },
  isB2B: false,
  showEverydayFinancing: false,
  isPlp: false,
  hideConsumerCardMessaging: false
};

ConsumerCardMessaging.themeProps = {
  showHDCC: bool
};

ConsumerCardMessaging.defaultThemeProps = {
  showHDCC: true
};
